import { useOrganizations } from '@consigli/hooks';
import { Organization } from '@consigli/types';
import { formatEnumToReadableText } from '@consigli/utils';
import { useCallback, useMemo, useState } from 'react';

import { DataTable, ObjectDetailView, Search } from '@/components';
import { formatTimestamp } from '@/utils/timestamp-converting';

import { EditOrganizationForm } from '../edit-organization-form';
import { OrganizationsActionButton } from './organizations-action-button';

export const OrganizationsTable = () => {
  const { organizations, isLoading } = useOrganizations();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOrganizations = useMemo(() => {
    if (!searchTerm) return organizations;
    const lower = searchTerm.toLowerCase();
    return organizations.filter(
      (org) => org.name.toLowerCase().includes(lower) || org.id.toLowerCase().includes(lower),
    );
  }, [organizations, searchTerm]);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState<Organization | null>(null);

  const handleViewDetails = useCallback((organization: Organization) => {
    setSelectedOrganization(organization);
    setIsDetailViewOpen(true);
  }, []);

  const handleEdit = useCallback((organization: Organization) => {
    setEditingOrganization(organization);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setEditingOrganization(null);
    // RTK Query automatically refetches via cache invalidation tags
  }, []);

  const columns = [
    {
      header: 'Name',
      accessorKey: 'name',
    },
    {
      header: 'Type',
      accessorKey: 'type',
      cell: ({ row }: { row: { original: { type: string } } }) => {
        return <span>{formatEnumToReadableText(row.original.type)}</span>;
      },
    },
    {
      header: 'Created At',
      accessorKey: 'createdAt',
      cell: ({ row }: { row: { original: { createdAt: string | number | Date } } }) => {
        return <span>{formatTimestamp(String(row.original.createdAt))}</span>;
      },
    },
    {
      header: 'Actions',
      accessorKey: 'actions',
      cell: ({ row }: { row: { original: Organization } }) => {
        return (
          <OrganizationsActionButton
            organization={row.original}
            onViewDetails={handleViewDetails}
            onEdit={handleEdit}
          />
        );
      },
    },
  ];

  return (
    <>
      <div className="mb-4">
        <Search
          className="w-[250px]"
          text={searchTerm}
          placeholder="Search for organizations"
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <DataTable
        columns={columns}
        data={filteredOrganizations}
        isLoading={isLoading}
        scrollable
        height="60vh"
      />

      {selectedOrganization && (
        <ObjectDetailView
          object={selectedOrganization}
          objectType="Organization"
          summary={`Organization "${selectedOrganization.name}" details including type and configuration.`}
          isOpen={isDetailViewOpen}
          onOpenChange={(open: boolean) => {
            setIsDetailViewOpen(open);
            if (!open) {
              setSelectedOrganization(null);
            }
          }}
        />
      )}

      {editingOrganization && (
        <EditOrganizationForm
          organization={editingOrganization}
          onSuccess={handleEditSuccess}
          open={!!editingOrganization}
          onOpenChange={(open) => {
            if (!open) {
              setEditingOrganization(null);
            }
          }}
        />
      )}
    </>
  );
};
